import {
  // plate size details fields
  WELL_FIELD,
  SCALE_FIELD,
  PURIFICATION_FIELD,
  BUFFER_FIELD,
  SHIPPING_FORMAT_FIELD,

  // normalization aliquots fields
  NORMALIZATION,
  VOLUME,
  VOLUME_UNIT,
  CONCENTRATION,
  CONCENTRATION_UNIT,
  ALIQUOTS_CHECKED,
  ALIQUOTS_NUMBER,
  ALIQUOTS_VOLUME,
  REMAINDER_CHECKED,

  // plate pooling
  PLATE_POOLING,
  POOLED_PLATES,
  POOLED_PLATE_NAME,
  POOLED_PLATE_VOLUME,
  // namespaces
  FILE_NAMESPACE,
  PLATE_SIZE_DETAILS_NAMESPACE,
  NORMALIZATION_ALIQUOTS_NAMESPACE,
  PLATE_POOLING_NAMESPACE,
  REMAINDER_TREATMENT,
  REMAINDER_PLATE_TYPE,
  // form data
  PLATE_POOLING_OPTIONS,
  NORMALIZATION_OPTIONS,
  REMAINDER_VOLUME,
  REMAINDERS_NAMESPACE,
  STEPS,
  COUNTRY_CODE_JAPAN,
  DESIGN_ID
} from 'src/constants/forms';
import messages from 'src/messages/messages';

export const getPooledPlatesListData = (platePoolingData) => {
  const selectedPooling = platePoolingData[PLATE_POOLING]?.value;

  if (selectedPooling === PLATE_POOLING_OPTIONS.NONE || !selectedPooling) {
    return {};
  }

  const volume = platePoolingData[POOLED_PLATE_VOLUME]?.value;
  const poolList = (platePoolingData[POOLED_PLATES]?.value || []).map((item) => ({
    ...item,
    volume: item.volume || volume,
  }));

  return { poolList };
};

export const isUploadFileValid = (dnaData) => {
  const fileData = dnaData[FILE_NAMESPACE] || {};

  return fileData.status !== 'ERROR' &&
    fileData.uploaded &&
    fileData.processed &&
    !fileData?.errorMessages?.length &&
    !(fileData?.data?.plateStatuses || []).find((item) => item.errors?.length);
};

export const isPlateSizeDetailsValid = (dnaData) => {
  const plateSizeData = dnaData[PLATE_SIZE_DETAILS_NAMESPACE] || {};

  return (
    plateSizeData[WELL_FIELD]?.value &&
    plateSizeData[SCALE_FIELD]?.value &&
    plateSizeData[PURIFICATION_FIELD]?.value &&
    plateSizeData[BUFFER_FIELD]?.value &&
    plateSizeData[SHIPPING_FORMAT_FIELD]?.value &&
    plateSizeData[DESIGN_ID]?.isValid
  );
};

export const isRemaindersValid = (remaindersData = {}) => {
  const hasRemainders = remaindersData[REMAINDER_CHECKED]?.value;
  const remainderTreatment = remaindersData[REMAINDER_TREATMENT]?.value;
  const remainderPlateType = remaindersData[REMAINDER_PLATE_TYPE]?.value;

  return !hasRemainders || (
    remainderTreatment &&
    remainderPlateType &&
    remaindersData[REMAINDER_TREATMENT]?.isValid &&
    remaindersData[REMAINDER_PLATE_TYPE]?.isValid &&
    remaindersData[REMAINDER_VOLUME]?.isValid
  );
};

export const isNormalizationAliquotsValid = (dnaData) => {
  const normalizationAliquotsData = dnaData[NORMALIZATION_ALIQUOTS_NAMESPACE] || {};
  const normalization = normalizationAliquotsData[NORMALIZATION]?.value;
  const volumeNormalization = normalizationAliquotsData[VOLUME]?.value;
  const volumeNormalizationUom = normalizationAliquotsData[VOLUME_UNIT]?.value;
  const concentrationNormalization = normalizationAliquotsData[CONCENTRATION]?.value;
  const concentrationNormalizationUom = normalizationAliquotsData[CONCENTRATION_UNIT]?.value;
  const hasAliquots = normalizationAliquotsData[ALIQUOTS_CHECKED]?.value;
  const aliquotsQty = normalizationAliquotsData[ALIQUOTS_NUMBER]?.value;
  const aliquotsVolume = normalizationAliquotsData[ALIQUOTS_VOLUME]?.value;

  if (normalization === NORMALIZATION_OPTIONS.NONE) {
    return true;
  }

  if (normalization === NORMALIZATION_OPTIONS.CONCENTRATION_VOLUME) {
    return (
      volumeNormalization &&
      volumeNormalizationUom &&
      concentrationNormalization &&
      concentrationNormalizationUom &&
      normalizationAliquotsData[VOLUME]?.isValid &&
      normalizationAliquotsData[VOLUME_UNIT]?.isValid &&
      normalizationAliquotsData[CONCENTRATION]?.isValid &&
      normalizationAliquotsData[CONCENTRATION_UNIT]?.isValid
    );
  }

  const aliquotsValid = !hasAliquots || (
    hasAliquots &&
    aliquotsQty &&
    aliquotsVolume &&
    normalizationAliquotsData[ALIQUOTS_NUMBER]?.isValid &&
    normalizationAliquotsData[ALIQUOTS_VOLUME]?.isValid
  );

  const remaindersValid = isRemaindersValid(dnaData[REMAINDERS_NAMESPACE]);

  if (normalization === NORMALIZATION_OPTIONS.VOLUME) {
    return (
      volumeNormalization &&
      volumeNormalizationUom &&
      normalizationAliquotsData[VOLUME]?.isValid &&
      normalizationAliquotsData[VOLUME_UNIT]?.isValid &&
      aliquotsValid &&
      remaindersValid
    );
  }

  if (normalization === NORMALIZATION_OPTIONS.CONCENTRATION) {
    return (
      concentrationNormalization &&
      concentrationNormalizationUom &&
      normalizationAliquotsData[CONCENTRATION]?.isValid &&
      normalizationAliquotsData[CONCENTRATION_UNIT]?.isValid &&
      aliquotsValid &&
      remaindersValid
    );
  }

  return false;
};

export const isPlatePoolingValid = (dnaData) => {
  const fileData = dnaData[FILE_NAMESPACE] || {};
  const platePoolingData = dnaData[PLATE_POOLING_NAMESPACE] || {};
  const pooling = platePoolingData[PLATE_POOLING]?.value;
  const pooledPlates = platePoolingData[POOLED_PLATES]?.value || [];
  const pooledPlateVolume = platePoolingData[POOLED_PLATE_VOLUME]?.value;
  const remaindersValid = isRemaindersValid(dnaData[REMAINDERS_NAMESPACE]);

  if (pooling === PLATE_POOLING_OPTIONS.NONE) {
    return true;
  }

  if (pooling === PLATE_POOLING_OPTIONS.CUSTOM) {
    return !!(remaindersValid &&
      pooledPlates.length &&
      platePoolingData[POOLED_PLATE_VOLUME]?.isValid &&
      platePoolingData[POOLED_PLATE_NAME]?.isValid &&
      pooledPlates.length === fileData.data.plateStatuses.length / 2
    );
  }

  if (pooling === PLATE_POOLING_OPTIONS.HALVES || pooling === PLATE_POOLING_OPTIONS.ODD_EVEN) {
    return remaindersValid && pooledPlateVolume && platePoolingData[POOLED_PLATE_VOLUME]?.isValid;
  }

  return false;
};

export const DEFAULT_DNA_STEPS = [{
  order: 0,
  isActive: true,
  isComplete: false,
  isVisited: true,
  title: messages.uploadPlateTemplate,
  name: STEPS.UPLOAD_FILE
},
{
  order: 1,
  isActive: false,
  isComplete: false,
  isVisited: false,
  title: messages.plateSizeDetails,
  name: STEPS.PLATE_SIZE_DETAILS
},
{
  order: 2,
  isActive: false,
  isComplete: false,
  isVisited: false,
  title: messages.normalizationAndAliquots,
  name: STEPS.NORMALIZATION_ALIQUOTS
},
{
  order: 3,
  isActive: false,
  isComplete: false,
  isVisited: false,
  isOptional: true,
  title: messages.platePooling,
  name: STEPS.PLATE_POOLING
},
{
  order: 4,
  isActive: false,
  isComplete: false,
  isVisited: false,
  title: messages.reviewAndAddToCart,
  name: STEPS.REVIEW_AND_SUBMIT
}];

export const DNA_STEPS = { [COUNTRY_CODE_JAPAN]: [{
  order: 0,
  isActive: true,
  isComplete: false,
  isVisited: true,
  title: messages.uploadPlateTemplate,
  name: STEPS.UPLOAD_FILE
},
{
  order: 1,
  isActive: false,
  isComplete: false,
  isVisited: false,
  title: messages.plateSizeDetails,
  name: STEPS.PLATE_SIZE_DETAILS
},
{
  order: 2,
  isActive: false,
  isComplete: false,
  isVisited: false,
  title: messages.reviewAndAddToCart,
  name: STEPS.REVIEW_AND_SUBMIT
}] };
